import React from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  ListItemText,
  OutlinedInput,
  Typography,
  Paper,
  Chip,
  Stack,
} from "@mui/material";
import { deleteRequest, getRequest, putRequest } from "Includes/api/apicall";
import { GET_URL, PUT_URL } from "Includes/urls";
import { Link, useNavigate } from "react-router-dom";
import { AddCircleOutlineOutlined, EditOutlined, DeleteOutlined, PeopleAltOutlined } from "@mui/icons-material";
import Swal from "sweetalert2";
import { validateMobileNumberFormat } from "Includes/functions";

function UsersList(props) {
  const navigate = useNavigate();
  const [rows, setRows] = React.useState([]);
  const [rowCount, setRowCount] = React.useState(0);
  const [loading, setLoading] = React.useState(true);

  // ✅ FIXED PAGINATION (same as CompanyList)
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(10);

  // ✅ FILTERS
  const [nameInput, setNameInput] = React.useState("");
  const [nameSearch, setNameSearch] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("all");

  const latestRequestRef = React.useRef(0);
  const userInfo = React.useMemo(() => JSON.parse(localStorage.getItem("user") || "{}"), []);
  const allowedCodes = React.useMemo(() => {
    if (Array.isArray(userInfo?.permission_codenames) && userInfo.permission_codenames.length) {
      return new Set(userInfo.permission_codenames.filter(Boolean));
    }
    const ownCodes = (userInfo?.user_permissions || userInfo?.permissions || []).map((p) =>
      typeof p === "string" ? p : p?.codename
    );
    const groupCodes = (userInfo?.groups || [])
      .flatMap((g) => g?.permissions || [])
      .map((p) => (typeof p === "string" ? p : p?.codename));
    return new Set([...ownCodes, ...groupCodes].filter(Boolean));
  }, [userInfo]);
  const isSuperuser = Boolean(userInfo?.is_superuser);
  const canCreate = isSuperuser || allowedCodes.has("visible_user_add");
  const canUpdate = isSuperuser || allowedCodes.has("visible_user_change");
  const canDelete = isSuperuser || allowedCodes.has("visible_user_delete");
  const [totalCounts, setTotalCounts] = React.useState({ total: null, active: null, inactive: null });

  const fetchTotalCounts = React.useCallback(() => {
    // Fetch all three counts in parallel using limit=1 just to read the count field
    Promise.all([
      getRequest(GET_URL.user.api, { limit: 1, pageno: 1 }, props),
      getRequest(GET_URL.user.api, { limit: 1, pageno: 1, status: 'active' }, props),
      getRequest(GET_URL.user.api, { limit: 1, pageno: 1, status: 'inactive' }, props),
    ]).then(([all, active, inactive]) => {
      setTotalCounts({
        total: all?.data?.count ?? null,
        active: active?.data?.count ?? null,
        inactive: inactive?.data?.count ?? null,
      });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [editingUser, setEditingUser] = React.useState(null);
  const [groupsList, setGroupsList] = React.useState([]);
  const [saving, setSaving] = React.useState(false);

  const [editForm, setEditForm] = React.useState({
    username: "",
    email: "",
    mobile_num: "",
    is_staff: false,
    groups: [],
  });

  const [editFormErrors, setEditFormErrors] = React.useState({});

  // 🔥 DEBOUNCE SEARCH
  React.useEffect(() => {
    const t = setTimeout(() => {
      const trimmed = nameInput.trim();
      setNameSearch((prev) => {
        if (prev !== trimmed) {
          setPage(0);
        }
        return trimmed;
      });
    }, 400);
    return () => clearTimeout(t);
  }, [nameInput]);

  // 🔥 FETCH USERS
  React.useEffect(() => {
    getUsersList(page, pageSize);
    getGroupsList();
    fetchTotalCounts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, nameSearch, statusFilter]);

  const getUsersList = (nextPage = page, nextPageSize = pageSize) => {
    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;

    setLoading(true);

    const params = {
      limit: nextPageSize,
      pageno: nextPage + 1,
    };

    if (nameSearch) params.username = nameSearch;
    if (statusFilter !== "all") params.status = statusFilter;

    getRequest(GET_URL.user.api, params, props)
      .then((res) => {
        if (latestRequestRef.current !== requestId) return;

        const data = res?.data?.data || [];
        setRows(data);
        setRowCount(res?.data?.count || data.length);
      })
      .finally(() => {
        if (latestRequestRef.current === requestId) {
          setLoading(false);
        }
      });
  };

  const getGroupsList = () => {
    getRequest(GET_URL.groups.api, {}, props)
      .then((res) => {
        setGroupsList(
          (res?.data?.data || []).filter((g) => g.id !== 1)
        );
      })
      .catch(() => setGroupsList([]));
  };

  const handleDelete = (row) => {
    if (!row?.id) return;
    if (row.id === userInfo?.id) {
      Swal.fire({ icon: "warning", title: "You cannot delete your own user." });
      return;
    }
    deleteRequest(
      `${GET_URL.user.api}${row.id}/`,
      {},
      { ...props, confirmButtonText: "Yes, delete user!" }
    ).then((response) => {
      if (response && response.status === 200) {
        Swal.fire({
          position: "top-end",
          icon: "success",
          title: "User deactivated",
          showConfirmButton: false,
          timer: 1400,
        });
        getUsersList(page, pageSize);
      }
    });
  };

  // 🔥 HANDLERS
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0); // important
  };

  // 🔥 COLUMNS
  const columns = [
    { field: "username", headerName: "User", flex: 1, minWidth: 150 },
    { field: "email", headerName: "Email", flex: 1, minWidth: 180 },
    { field: "mobile_num", headerName: "Mobile", flex: 1, minWidth: 140 },
    {
      field: "is_active",
      headerName: "Status",
      flex: 1,
      minWidth: 120,
      renderCell: (p) => (
        <Chip
          label={p.value ? "Active" : "Inactive"}
          color={p.value ? "success" : "error"}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: "is_staff",
      headerName: "Staff",
      flex: 1,
      minWidth: 120,
      renderCell: (p) => (
        <Chip
          label={p.value ? "Yes" : "No"}
          color={p.value ? "primary" : "default"}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: "groups_names",
      headerName: "Groups",
      flex: 1.5,
      minWidth: 180,
      renderCell: (p) =>
        p.value?.length ? p.value.join(", ") : "-",
    },
    {
      field: "created",
      headerName: "Created",
      flex: 1,
      minWidth: 130,
      renderCell: (p) =>
        p.value
          ? new Date(p.value).toLocaleDateString()
          : "-",
    },
    {
      field: "created_by_name",
      headerName: "Created By",
      flex: 1,
      minWidth: 150,
      valueGetter: (p) => p.row?.created_by_name || "-",
    },
    {
      field: "modified_by_name",
      headerName: "Modified By",
      flex: 1,
      minWidth: 150,
      valueGetter: (p) => p.row?.modified_by_name || "-",
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      minWidth: 200,
      flex: 0.6,
      renderCell: (params) => (
        <Stack direction="row" spacing={1} alignItems="center">
          {canUpdate && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditOutlined fontSize="small" />}
              onClick={() => navigate(`/user/add?id=${params.row.id}`)}
            >
              Edit
            </Button>
          )}
          {canDelete && (
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<DeleteOutlined fontSize="small" />}
              disabled={!params.row?.is_active || params.row?.id === userInfo?.id}
              onClick={() => handleDelete(params.row)}
            >
              Deactivate
            </Button>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* HEADER */}
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={6}>
          <Typography variant="h5" fontWeight={600}>
            User Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage users
          </Typography>
        </Grid>

        <Grid item xs={12} md={6} textAlign="right">
          {canCreate && (
            <Button
              variant="contained"
              component={Link}
              to="/user/add"
              startIcon={<AddCircleOutlineOutlined />}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                px: 2,
                py: 1,
                boxShadow: "0 4px 14px rgba(0,0,0,0.1)",
              }}
            >
              Add User
            </Button>
          )}
        </Grid>
      </Grid>



      {/* FILTER */}
      <Paper
        elevation={0}
        sx={{
          mt: 3,
          p: 2,
          borderRadius: "16px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
        }}
      >
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Search by username"
              placeholder="Type to filter..."
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              variant="outlined"
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* TABLE */}
      <Paper
        elevation={0}
        sx={{
          mt: 3,
          p: 2,
          borderRadius: "16px",
          boxShadow: "0 6px 20px rgba(0,0,0,0.05)",
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          pagination
          paginationMode="server"

          // ✅ FIXED PAGINATION
          page={page}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}

          rowsPerPageOptions={[10, 20, 50]}
          rowCount={rowCount}
          autoHeight
          disableSelectionOnClick
          sx={{
            border: "none",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f5f7fb",
              fontWeight: 600,
            },
            "& .MuiDataGrid-row": {
              borderRadius: "8px",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid #f0f0f0",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#fafafa",
            },
          }}
        />
      </Paper>
    </Box>
  );
}

export default UsersList;